/**
 * This file is categorized as "Proprietary Framework Code"
 * and is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 */
import { useState, useEffect, } from 'react';
import {
	Column,
	FlatList,
	Icon,
	Pressable,
	Row,
	Text,
} from 'native-base';
import {
	FontAwesome5,
} from '@expo/vector-icons';
import testProps from '../functions/testProps';
import useForceUpdate from '../hooks/useForceUpdate';
import Loading from './Loading';
import Footer from './Footer';
import Toolbar from './Toolbar/Toolbar';
import PaginationToolbar from './Toolbar/PaginationToolbar';
import NoRecordsFound from './NoRecordsFound';
import _ from 'lodash';

export default function GridPanel(props) {
	if (!props.Repository) {
		return null;
	}
	const {
			Repository,
			topToolbar = null,
			bottomToolbar = 'pagination',
			columns = [],
			columnProps = {},
			getRowProps = () => {
				return {
					bg: '#fff',
					p: 2,
				};
			},
			onSelect = () => {},
			initialScrollIndex = 0,
			flatListProps = {},
			pullToRefresh = true,
			hideRightColumn = false,
			disableLoadingIndicator = false,
			noneFoundText,
		} = props,
		entities = Repository.entities,
		[isLoading, setIsLoading] = useState(false),
		forceUpdate = useForceUpdate(),
		
		columnRenderer = (entity) => {
			if (_.isArray(columns)) {
				return _.map(columns, (config, key) => {
					let value;

					if (_.isObject(config)) {
						if (config.renderer) {
							return config.renderer(entity, key);
						}
					}
					if (_.isString(config)) {
						value = entity[config];
					}
					if (_.isFunction(config)) {
						value = config(entity);
					}
					
					if (_.isFunction(value)) {
						return value(key);
					}

					const propsToPass = columnProps[key] || {};
					return <Text key={key} flex={1} {...propsToPass}>{value}</Text>;
				});
			} else {
				// TODO: if 'columns' is an object, parse its contents
				throw new Error('Non-array columns not yet supported');
			}
		};
	
	useEffect(() => {
		if (!Repository || !Repository.on) {
			return;
		}
		if (disableLoadingIndicator) {
			return;
		}
		const setTrue = () => setIsLoading(true),
			setFalse = () => setIsLoading(false);

		Repository.on('beforeLoad', setTrue);
		Repository.on('load', setFalse);
		Repository.on('change', forceUpdate);
		
		return () => {
			Repository.off('beforeLoad', setTrue);
			Repository.off('load', setFalse);
			Repository.off('change', forceUpdate);
		};
	}, [Repository]);
	
	return <Column
				{...testProps('GridPanelContainer')}
				flex={1}
				w="100%"
			>
				{topToolbar && <Toolbar>{topToolbar}</Toolbar>}
				
				{Repository.isLoading && <Column flex={1}><Loading /></Column>}

				{!Repository.isLoading && (!entities.length ? <NoRecordsFound text={noneFoundText} /> : 
					<FlatList
						refreshing={isLoading}
						onRefresh={pullToRefresh ? () => { Repository.load() } : null}
						progressViewOffset={100}
						data={entities}
						keyExtractor={(entity) => {
							return String(entity.id);
						}}
						initialScrollIndex={initialScrollIndex}
						initialNumToRender={entities.length}
						renderItem={(row) => {
							const entity = row.item,
								rowProps = getRowProps ? getRowProps(entity) : {};
							return <Pressable
										{...testProps(Repository.schema.name + '-' + entity.id)}
										onPress={() => onSelect(entity)}
										onLongPress={() => onSelect(entity)}
									>
										<Row
											alignItems="center"
											borderBottomWidth={1}
											borderBottomColor="trueGray.500"
											{...rowProps}
											>
												{columnRenderer(entity)}
												{!hideRightColumn && <Icon
													as={FontAwesome5}
													name="angle-right"
													color="#aaa"
													variant="ghost"
													w={30}
													alignSelf="center"
													ml={3}
												/>}
										</Row>
									</Pressable>;
						}}
						bg="trueGray.200"
						{...flatListProps}
					/>)}

				{bottomToolbar && (bottomToolbar !== 'pagination' || entities.length > 5) &&
					<Footer pt={0} pb={0} px={0}>
						{bottomToolbar === 'pagination' ? 
							<PaginationToolbar Repository={Repository} /> :
								<Toolbar>{bottomToolbar}</Toolbar>
							}
					</Footer>}
			</Column>;
}
